import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import RefreshIcon from "@mui/icons-material/Refresh";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import { generateAuditedClient } from "../audit/auditedClient";
import { revokeShareLink, updateShareSnapshot } from "../graphql/mutations";
import {
  RevokeShareLinkInput,
  ShareLink,
  UpdateShareSnapshotInput,
} from "../models/API";

interface ShareLinkDialogProps {
  open: boolean;
  onClose: () => void;
  shareLink: ShareLink | null;
  userId: number;
  onRevoked?: () => void;
  onUpdated?: (updated: ShareLink) => void;
}

type SnackSeverity = "success" | "error";

// Extract a human-readable message from a GraphQL error, falling back to a
// generic one. The backend raises user-readable ValidationErrors (e.g. the
// "conversation is too large to share" size cap), which arrive as the first
// entry of the AppSync `errors` array.
function errorMessageOf(err: any, fallback: string): string {
  const message = err?.errors?.[0]?.message ?? err?.message;
  return typeof message === "string" && message.length > 0 ? message : fallback;
}

// Render the snapshot capture time as "Snapshot taken at <time>" using the
// last explicit update timestamp, falling back to creation time (Req 3.3).
function formatSnapshotTime(link: ShareLink): string {
  const ts = link.snapshot_updated_at ?? link.created_at;
  if (!ts) {
    return "Snapshot taken at an unknown time";
  }
  // AWSTimestamp is Unix epoch seconds.
  const when = new Date(ts * 1000).toLocaleString();
  return `Snapshot taken at ${when}`;
}

const ShareLinkDialog: React.FC<ShareLinkDialogProps> = ({
  open,
  onClose,
  shareLink,
  userId,
  onRevoked,
  onUpdated,
}) => {
  // Local copy so the dialog can reflect snapshot-time refreshes and the
  // revoked state without mutating the parent's state directly.
  const [link, setLink] = useState<ShareLink | null>(shareLink);
  const [updating, setUpdating] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMessage, setSnackMessage] = useState("");
  const [snackSeverity, setSnackSeverity] = useState<SnackSeverity>("success");

  const client = generateAuditedClient();

  // Keep the local link in sync whenever the parent supplies a new ShareLink
  // (e.g. a freshly generated link after reopening the dialog).
  useEffect(() => {
    setLink(shareLink);
  }, [shareLink]);

  const showSnack = (message: string, severity: SnackSeverity) => {
    setSnackMessage(message);
    setSnackSeverity(severity);
    setSnackOpen(true);
  };

  const isRevoked = !link || link.status === "revoked";

  // Build the share URL from the current app origin so the link always points
  // at the environment the user generated it in (alpha/gamma/prod/cell). The
  // backend-returned share_url uses a non-environment-specific placeholder
  // host, so the token is the source of truth and we construct the URL here.
  const shareUrl = link
    ? `${window.location.origin}/?view=shared&token=${link.share_token}`
    : "";

  // Copy the full Share_Link to the clipboard and confirm visibly (Req 1.6, 1.7).
  const handleCopy = async () => {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      showSnack("Link copied to clipboard", "success");
    } catch (err) {
      console.error("[ShareLinkDialog] Clipboard copy failed:", err);
      showSnack("Could not copy the link. Please copy it manually.", "error");
    }
  };

  // Re-capture the conversation's current turns into the SAME token (Req 3.1,
  // 3.3, 3.5). On success refresh the timestamp; on failure keep prior state.
  const handleUpdateSnapshot = async () => {
    if (!link || isRevoked || updating) return;
    setUpdating(true);
    try {
      const input: UpdateShareSnapshotInput = {
        share_token: link.share_token,
        user_id: userId,
      };
      const result = await client.graphql({
        query: updateShareSnapshot,
        variables: { input },
      });
      const updated = result.data.updateShareSnapshot;
      if (!updated) {
        throw new Error("No share link returned");
      }
      setLink(updated);
      onUpdated?.(updated);
      showSnack("Snapshot updated", "success");
    } catch (err) {
      console.error("[ShareLinkDialog] Update snapshot failed:", err);
      // Preserve the prior snapshot/timestamp on failure (Req 3.5). Surface
      // the backend's message when it has one (e.g. the share size cap).
      showSnack(
        errorMessageOf(err, "Could not update the snapshot. Please try again."),
        "error"
      );
    } finally {
      setUpdating(false);
    }
  };

  // Revoke the active link (Req 7.1-7.4). On success reflect the revoked
  // state; on failure keep the prior status and surface an error.
  const handleRevoke = async () => {
    if (!link || isRevoked || revoking) return;
    setRevoking(true);
    try {
      const input: RevokeShareLinkInput = {
        share_token: link.share_token,
        user_id: userId,
      };
      const result = await client.graphql({
        query: revokeShareLink,
        variables: { input },
      });
      const response = result.data.revokeShareLink;
      if (!response || !response.success) {
        throw new Error("Revoke did not complete");
      }
      setLink({ ...link, status: "revoked" });
      onRevoked?.();
      showSnack("Share link revoked", "success");
    } catch (err) {
      console.error("[ShareLinkDialog] Revoke failed:", err);
      // Retain the prior status on failure (Req 7.4).
      showSnack("Could not revoke the link. Please try again.", "error");
    } finally {
      setRevoking(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Share conversation</DialogTitle>
      <DialogContent>
        {link ? (
          <Box sx={{ mt: 1 }}>
            {isRevoked ? (
              <Alert severity="info" sx={{ mb: 2 }}>
                This share link has been revoked and can no longer be opened.
              </Alert>
            ) : (
              <Typography variant="body2" sx={{ mb: 1, color: "#555" }}>
                Anyone with this link who is signed in can view a read-only copy
                of this conversation.
              </Typography>
            )}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                label="Share link"
                value={shareUrl}
                InputProps={{ readOnly: true }}
                onFocus={(e) => e.target.select()}
              />
              <Tooltip title="Copy link">
                <span>
                  <IconButton
                    onClick={handleCopy}
                    disabled={isRevoked}
                    color="primary"
                    aria-label="Copy share link"
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
            <Typography
              variant="caption"
              sx={{ display: "block", mt: 1.5, color: "#777" }}
            >
              {formatSnapshotTime(link)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" sx={{ mt: 2, color: "#777" }}>
            No share link is available.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {!isRevoked && (
          <>
            <Button
              onClick={handleUpdateSnapshot}
              disabled={updating || revoking}
              startIcon={
                updating ? <CircularProgress size={16} /> : <RefreshIcon />
              }
            >
              Update snapshot
            </Button>
            <Button
              onClick={handleRevoke}
              disabled={updating || revoking}
              color="error"
              startIcon={
                revoking ? <CircularProgress size={16} /> : <LinkOffIcon />
              }
            >
              Revoke
            </Button>
          </>
        )}
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
      <Snackbar
        open={snackOpen}
        autoHideDuration={4000}
        onClose={() => setSnackOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackOpen(false)}
          severity={snackSeverity}
          sx={{ width: "100%" }}
        >
          {snackMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ShareLinkDialog;
